import { useTranslations } from "next-intl";

import { NestedSuggestionOption } from "@/components/suggestions/list/NestedSuggestionOption";
import { SuggestionOption } from "@/components/suggestions/list/SuggestionListOption";
import {
  getSuggestionAriaHotkey,
  getSuggestionHotkey,
  hasNestedItems,
} from "@/components/suggestions/list/utils";

import type { SuggestionsGroup } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { SuggestionsModel } from "@/models/SuggestionsModel";

type Props = {
  doc: DocumentModel;
  suggestions: SuggestionsModel;
  group: SuggestionsGroup;
  pseudoFocusIndex?: number;
  showHotKeys?: boolean;
  listId: string | null;
};

export function SuggestionsList({
  doc,
  suggestions,
  group,
  pseudoFocusIndex,
  showHotKeys,
  listId,
}: Props) {
  const t = useTranslations("suggestions");

  return (
    // The menu already names the suggestions; a named list inside it is announced twice.
    <ul role="none" data-testid="suggestions-list">
      {group.items.map((item, itemIndex) => {
        const key = `${item.type}-${itemIndex}`;
        const flatIndex = (group.startIndex ?? 0) + itemIndex;
        let hotkey: string | undefined;
        let ariaHotkey: string | undefined;
        if (item.hotkey) {
          hotkey = item.hotkey;
        } else if (showHotKeys) {
          hotkey = getSuggestionHotkey(flatIndex);
          ariaHotkey = getSuggestionAriaHotkey(flatIndex);
        }

        if (hasNestedItems(item) && item.type === "single") {
          return (
            <li key={key} role="none">
              <NestedSuggestionOption
                doc={doc}
                suggestions={suggestions}
                index={flatIndex}
                suggestion={item}
                listId={listId}
                indicator={item.indicatorIntlName ? t(item.indicatorIntlName) : undefined}
              />
            </li>
          );
        }

        return (
          <li key={key} role="none">
            <SuggestionOption
              doc={doc}
              suggestions={suggestions}
              index={flatIndex}
              suggestion={item}
              hotkey={hotkey}
              ariaHotkey={ariaHotkey}
              isFocused={pseudoFocusIndex === flatIndex}
              listId={listId}
            />
          </li>
        );
      })}
    </ul>
  );
}
